import React, { useEffect } from 'react';
import { actions } from 'redux/actions';
import { ACTIONS_TYPES } from 'redux/helper';
import { ROUTE } from 'constant';
import Shared from 'screens/shared';
import { useDispatch, useSelector } from 'react-redux';
import { form } from 'types';
import { createLink, getKey } from 'utils';
import { _t } from 'utils/translation';
import { Tags } from './tags';
import { accessTokenForm, editRegistryForm } from '../config';

export const Single = ({ match, history }) => {
  const dispatch = useDispatch();
  const { id } = match.params;
  const {
    [ACTIONS_TYPES.CONTAINER_REGISTRY.REGISTRY.GET]: registry = {},
    [ACTIONS_TYPES.CONTAINER_REGISTRY.TOKEN.GET_ALL]: tokens = [],
    [ACTIONS_TYPES.CONTAINER_REGISTRY.REPOSITORY.GET_ALL]: repo = {},
    [ACTIONS_TYPES.CONTAINER_REGISTRY.TOKEN.CREATE]: createdToken = null,
  } = useSelector((e) => e);

  useEffect(() => {
    actions.containerRegistry.registry.get({ id });
    actions.containerRegistry.token.getAll({ registryId: id });
    actions.containerRegistry.repository.getAll({ registryId: id });
  }, [id]);

  const submitTokenHandler = (props) => {
    const properties = { ...props };
    if (properties.scopes) {
      properties.scopes = properties.scopes.map((e) => {
        const item = ({ ...e, name: e.name || '*' });
        if (e.type === 'repository' && e.actions[0] === '*') {
          item.actions = ['pull', 'push', 'delete'];
        }
        return item;
      });
    }
    actions.containerRegistry.token.create({ registryId: id, properties });
  };

  const closeTokenDialogHandler = () => {
    actions.common.quickUpdate({ [ACTIONS_TYPES.CONTAINER_REGISTRY.TOKEN.CREATE]: null });
    actions.containerRegistry.token.getAll({ registryId: id });
  };

  const pad = (input, size = 2) => {
    let num = input.toString();
    while (num.length < size) num = `0${num}`;
    return num;
  };

  const getUTCTime = (timeString) => {
    // 15:00:00+00:00
    if (!timeString) return '';
    const isNegativeOffset = timeString.includes('-');
    const [time, zoneOffset = ''] = timeString.split(isNegativeOffset ? '-' : '+');
    const [offsetHours] = zoneOffset ? zoneOffset.split(':').map((item) => parseInt(item, 10)) : [0];
    const [hours, minutes, seconds] = time.split(':').map((item) => parseInt(item, 10));
    return `${pad(hours + (offsetHours * (isNegativeOffset ? 1 : -1)))}:${pad(minutes)}:${pad(seconds)}`;
  };

  const registryUpdateHandler = (properties) => {
    let { garbageCollectionSchedule = {} } = properties || {};

    garbageCollectionSchedule = {
      time: `${getUTCTime(garbageCollectionSchedule.time)}+00:00`,
      days: Array.isArray(garbageCollectionSchedule.days) ? garbageCollectionSchedule.days : [garbageCollectionSchedule.days],
    };
    // maintenanceWindow = {
    //   time: `${maintenanceWindow.time}`,
    //   days: Array.isArray(maintenanceWindow.days) ? maintenanceWindow.days : [maintenanceWindow.days],
    // };

    actions.containerRegistry.registry.update({ id, properties: { garbageCollectionSchedule } }).then((result) => {
      if (getKey('isAccessByDCD') && !result.httpStatus) {
        setTimeout(() => {
          actions.containerRegistry.registry.get({ id });
        }, 2000);
      }
    });
  };

  const registryDeleteHandler = () => {
    actions.containerRegistry.registry.delete({ id });
    history.push(createLink(ROUTE.DASHBOARD.CONTAINER_REGISTRY_LIST));
  };

  const getDockerLoginCommand = (token, dockerRegistry) => (
    `docker login -u="${
      token?.properties?.credentials?.username
    }" -p="${
      token?.properties?.credentials?.password
    }" ${
      dockerRegistry?.properties?.hostname
    }`
  );

  const { garbageCollectionSchedule = {}, maintenanceWindow = {} } = registry?.properties || {};

  return (
    <Shared.widgets.Page
      route={ROUTE.DASHBOARD.CONTAINER_REGISTRY_LIST}
      strapIcon="building"
      strapTitle={registry?.properties?.name}
      strapSubTitle={(
        <Shared.utilities.Status state={registry?.metadata?.state} />
      )}
      editForm={(
        <Shared.forms.Form
          {...editRegistryForm}
          onSubmit={registryUpdateHandler}
          submitTitle={_t('CONTAINER_REGISTRY.UPDATE_REGISTRY')}
          fieldsValues={{ garbageCollectionSchedule, maintenanceWindow }}
        />
      )}
      editAction={ACTIONS_TYPES.CONTAINER_REGISTRY.REGISTRY.UPDATE}
      onDelete={registryDeleteHandler}
    >
      <Shared.widgets.Properties
        properties={[
          [_t('CONTAINER_REGISTRY.PROPERTIES.HOST'), (
            <div className="flex flex-align-center flex-justify-between">
              <span>{registry?.properties?.hostname}</span>
              <Shared.utilities.CopyButton copyText={registry?.properties?.hostname} notificationMessage={_t('CONTAINER_REGISTRY.HOST_HAS_BEEN_COPIED')} />
            </div>
          )],
          [_t('CONTAINER_REGISTRY.PROPERTIES.LOCATION'), <Shared.utilities.Location location={registry?.properties?.location} />],
          ...(registry?.properties?.storageUsage ? [
            [
              _t('CONTAINER_REGISTRY.PROPERTIES.STORAGE_USAGE'),
              `${registry?.properties?.storageUsage?.bytes ? Number(registry?.properties?.storageUsage?.bytes / (1000 * 1000)).toLocaleString() : 0} MB`,
            ],
          ] : []),
          ...(registry?.properties?.garbageCollectionSchedule ? [
            [
              _t('CONTAINER_REGISTRY.PROPERTIES.GARBAGE_COLLECTION_SCHEDULE'),
              `${registry?.properties?.garbageCollectionSchedule?.days?.map((day) => _t(`days.${day.toLowerCase()}`)).join?.(',')}
                - ${getUTCTime(registry?.properties?.garbageCollectionSchedule?.time)}`,
            ],
          ] : []),
          [_t('CONTAINER_REGISTRY.PROPERTIES.CREATION_DATE'), new Date(registry?.metadata?.createdDate).toLocaleString()],
        ]}
      />

      <Shared.widgets.Section
        sectionTitle="Repositories"
        isAddButton={false}
        setItemTitle={(name) => name}
        items={repo?.[id]?.repositories || []}
        className="-m-t-25"
        setItemKey={(e) => e}
        setItemDetails={(name) => (<Tags repoName={name} registryId={id} />)}
        itemIcon="code-branch"
        itemOnDelete={(e) => {
          actions.containerRegistry.repository.delete({
            registryId: id,
            repoName: e,
          }).then(() => {
            actions.containerRegistry.repository.getAll({ registryId: id });
          });
          dispatch({
            type: ACTIONS_TYPES.CONTAINER_REGISTRY.REPOSITORY.GET_TAGS,
            payload: { [ACTIONS_TYPES.CONTAINER_REGISTRY.REPOSITORY.GET_TAGS]: { registryId: id, name: e, tags: null } },
          });
        }}
        itemOnClick={(e) => history.push(createLink(ROUTE.DASHBOARD.CONTAINER_REGISTRY_REPOSITORY, { id, repoName: e }))}
        refresh={() => actions.containerRegistry.repository.getAll({ registryId: id })}
      />

      <Shared.widgets.Section
        sectionTitle="Tokens"
        setItemTitle={({ properties = {} }) => properties?.name}
        items={tokens}
        className="-m-t-25"
        itemIcon="lock"
        setItemKey={(e) => e.id}
        itemOnEdit={({ id: tokenId, ...properties }) => actions.containerRegistry.token.update({ properties, tokenId, registryId: id })}
        createActionType={ACTIONS_TYPES.CONTAINER_REGISTRY.TOKEN.CREATE}
        itemOnDelete={(e) => actions.containerRegistry.token.delete({ registryId: id, tokenId: e.id })}
        createForm={form({
          onSubmit: submitTokenHandler,
          ...accessTokenForm(repo?.[id]?.repositories || []),
        })}
        setItemEditProps={({ id: tokenId, properties }) => ({ id: tokenId, status: properties?.status, expiryDate: properties?.expiryDate })}
      />

      <Shared.dialog.Dialog
        show={!!createdToken?.properties}
        isCloseButton
        closeHandler={closeTokenDialogHandler}
        content={(
          <div>
            <h3>
              Credentials:
              <Shared.utilities.CopyButton
                copyText={JSON.stringify(createdToken?.properties || {}, null, 4)}
                notificationMessage={_t('TEXT_HAS_BEEN_COPIED')}
              />
            </h3>
            <p>Please save these credentials, you will not be able to access it again.</p>
            <Shared.utilities.Code code={JSON.stringify(createdToken?.properties || {}, null, 4)} />
            <h3>
              Docker Login:
              <Shared.utilities.CopyButton
                copyText={getDockerLoginCommand(createdToken, registry)}
                notificationMessage={_t('TEXT_HAS_BEEN_COPIED')}
              />
            </h3>
            <Shared.utilities.Code
              code={getDockerLoginCommand(createdToken, registry)}
            />
          </div>
        )}
      />
    </Shared.widgets.Page>
  );
};


